import {
  createContext,
  useEffect,
  useContext,
  useState,
} from "react";

import aarData from "../data/aarData";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { useAuth } from "./AuthContext";
import { writeAuditLog } from "../firebase/audit";
import { getAppwriteFileUrl } from "../appwrite/appwrite";


const normalizeProjects = (projects) =>
  projects.map((project) => ({
    ...project,
    actions: Array.isArray(project.actions)
      ? project.actions.map((action, index) => ({
          ...action,
          id: action.id ?? `${project.id}-action-${index}`,
        }))
      : [],
  }));

const projectsCollection = collection(db, "aarProjects");
const localProjectsKey = "strides-aar-projects";

const getLocalProjects = () => {
  try {
    const storedProjects = window.localStorage.getItem(localProjectsKey);
    return storedProjects
      ? normalizeProjects(JSON.parse(storedProjects))
      : normalizeProjects(aarData);
  } catch {
    return normalizeProjects(aarData);
  }
};

const cacheProjects = (projects) => {
  try {
    window.localStorage.setItem(
      localProjectsKey,
      JSON.stringify(normalizeProjects(projects))
    );
  } catch {
    return;
  }
};

const saveProject = (project) =>
  setDoc(doc(db, "aarProjects", String(project.id)), project);


const AARDataContext =
  createContext(null);


// =====================================================
// AAR DATA PROVIDER
// =====================================================

export function AARDataProvider({
  children,
}) {
  const { isLoading: isAuthLoading, user } = useAuth();

  const [
    projects,
    setProjects,
  ] = useState(getLocalProjects);

  const [isLoading, setIsLoading] = useState(true);
  const [dataError, setDataError] = useState("");

  const recordAudit = (event) =>
    writeAuditLog({ ...event, user }).catch((error) => {
      console.error("Activity log write failed:", error);
      setDataError(
        "The change was saved, but activity tracking could not be recorded."
      );
    });

  useEffect(() => {
    let isMounted = true;

    if (isAuthLoading) {
      return () => {
        isMounted = false;
      };
    }

    const loadProjects = async () => {
      try {
        const snapshot = await getDocs(projectsCollection);

        if (snapshot.empty) {
          const seededProjects = normalizeProjects(aarData);
          await Promise.all(seededProjects.map(saveProject));

          if (isMounted) {
            setProjects(seededProjects);
            cacheProjects(seededProjects);
          }
        } else if (isMounted) {
          const firestoreProjects = snapshot.docs.map((projectDoc) => ({
            ...projectDoc.data(),
            id: projectDoc.data().id ?? (Number(projectDoc.id) || projectDoc.id),
          }));

          setProjects(normalizeProjects(firestoreProjects));
          cacheProjects(firestoreProjects);
        }
      } catch {
        if (isMounted) {
          setProjects(getLocalProjects());
          setDataError("Unable to load saved AAR data. Showing local data.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      isMounted = false;
    };
  }, [isAuthLoading, user]);


  // ===================================================
  // ADD NEW AAR
  // ===================================================

  const addAAR = async (newProject) => {

    const project = {

      ...newProject,

      id: Date.now(),

    };

    const normalizedProject = normalizeProjects([project])[0];


    await saveProject(normalizedProject);

    recordAudit({
      action: "CREATE_AAR",
      entityType: "aarProject",
      entityId: normalizedProject.id,
      entityTitle: normalizedProject.title,
    });
    recordAudit({
      action: "SUBMIT_AAR",
      entityType: "aarProject",
      entityId: normalizedProject.id,
      entityTitle: normalizedProject.title,
    });

    setProjects(
      (currentProjects) => [

        ...currentProjects,

        normalizedProject,

      ]
    );

    cacheProjects([...projects, normalizedProject]);


    return normalizedProject;

  };

  const addAARWithAppwriteFile = async (newProject, appwriteFile) => {
    const project = {
      ...newProject,
      id: Date.now(),
      attachment: {
        fileName: appwriteFile.name,
        fileId: appwriteFile.$id,
        fileUrl: getAppwriteFileUrl(appwriteFile.$id),
        fileType: appwriteFile.mimeType || "application/octet-stream",
        fileSize: Number(appwriteFile.sizeOriginal || 0),
        storagePath: "appwrite-storage",
        uploadedAt: new Date().toISOString(),
        uploadedBy: user?.uid || "unknown",
      },
    };
    const normalizedProject = normalizeProjects([project])[0];

    await saveProject(normalizedProject);

    recordAudit({
      action: "CREATE_AAR",
      entityType: "aarProject",
      entityId: normalizedProject.id,
      entityTitle: normalizedProject.title,
    });
    recordAudit({
      action: "UPLOAD_AAR_DOCUMENT",
      entityType: "aarProject",
      entityId: normalizedProject.id,
      entityTitle: normalizedProject.title,
      changes: normalizedProject.attachment,
    });
    recordAudit({
      action: "SUBMIT_AAR",
      entityType: "aarProject",
      entityId: normalizedProject.id,
      entityTitle: normalizedProject.title,
    });

    setProjects((currentProjects) => [
      ...currentProjects,
      normalizedProject,
    ]);
    cacheProjects([...projects, normalizedProject]);

    return normalizedProject;
  };
  // ===================================================
  // UPDATE EXISTING AAR
  // ===================================================

  const updateAAR = async (
    updatedProject
  ) => {

    const normalizedProject = normalizeProjects([updatedProject])[0];

    await saveProject(normalizedProject);

    recordAudit({
      action: "UPDATE_AAR",
      entityType: "aarProject",
      entityId: normalizedProject.id,
      entityTitle: normalizedProject.title,
    });

    setProjects(
      (currentProjects) =>

        currentProjects.map(
          (project) =>

            project.id ===
            normalizedProject.id

              ? normalizedProject

              : project
        )

    );

    cacheProjects(
      projects.map((project) =>
        project.id === normalizedProject.id
          ? normalizedProject
          : project
      )
    );

  };

  const approveAAR = async (project) => {
    const approvedProject = {
      ...project,
      status: "APPROVED",
    };

    await updateAAR(approvedProject);
    recordAudit({
      action: "APPROVE_AAR",
      entityType: "aarProject",
      entityId: project.id,
      entityTitle: project.title,
    });

    return approvedProject;
  };


  // ===================================================
  // DELETE AAR
  // ===================================================

  const deleteAAR = (
    id
  ) => {

    setProjects(
      (currentProjects) =>

        currentProjects.filter(
          (project) =>
            project.id !== Number(id)
        )

    );

    cacheProjects(
      projects.filter((project) => project.id !== Number(id))
    );

    deleteDoc(doc(db, "aarProjects", String(id))).catch(() => {
      setDataError("Unable to delete the AAR from Firestore.");
    });

    recordAudit({
      action: "DELETE_AAR",
      entityType: "aarProject",
      entityId: id,
      entityTitle: projects.find((project) => project.id === Number(id))?.title,
    });

  };


  // ===================================================
  // GET AAR BY ID
  // ===================================================

  const getAARById = (
    id
  ) => {

    return projects.find(
      (project) =>
        project.id === Number(id)
    );

  };


  // ===================================================
  // ADD ACTION TO AAR
  // ===================================================

  const addAction = (
    projectId,
    newAction
  ) => {

    const numericProjectId =
      Number(projectId);


    const action = {

      ...newAction,

      id:
        newAction.id ||
        `${numericProjectId}-action-${Date.now()}`,

    };

    const currentProject = projects.find(
      (project) => project.id === numericProjectId
    );

    if (currentProject) {
      const updatedProject = {
        ...currentProject,
        actions: [
          ...(Array.isArray(currentProject.actions)
            ? currentProject.actions
            : []),
          action,
        ],
      };

      cacheProjects(
        projects.map((project) =>
          project.id === numericProjectId
            ? updatedProject
            : project
        )
      );

      saveProject({
        ...updatedProject,
      }).catch(() => {
        setDataError("Unable to save the new action to Firestore.");
      });

      recordAudit({
        action: "CREATE_ACTION",
        entityType: "aarAction",
        entityId: action.id,
        entityTitle: action.action,
      });
    }


    setProjects(
      (currentProjects) =>

        currentProjects.map(
          (project) => {

            if (
              project.id !==
              numericProjectId
            ) {

              return project;

            }


            return {

              ...project,

              actions: [

                ...(Array.isArray(
                  project.actions
                )
                  ? project.actions
                  : []),

                action,

              ],

            };

          }
        )

    );


    return action;

  };


  // ===================================================
  // UPDATE ACTION
  // ===================================================

  const updateAction = (
    projectId,
    actionId,
    updates
  ) => {

    const numericProjectId =
      Number(projectId);

    const currentProject = projects.find(
      (project) => project.id === numericProjectId
    );

    if (currentProject) {
      const updatedProject = {
        ...currentProject,
        actions: (Array.isArray(currentProject.actions)
          ? currentProject.actions
          : []
        ).map((action) =>
          String(action.id) === String(actionId)
            ? { ...action, ...updates }
            : action
        ),
      };

      cacheProjects(
        projects.map((project) =>
          project.id === numericProjectId
            ? updatedProject
            : project
        )
      );

      saveProject({
        ...updatedProject,
      }).catch(() => {
        setDataError("Unable to save the action changes to Firestore.");
      });

      recordAudit({
        action: updates.status ? "CHANGE_ACTION_STATUS" : "UPDATE_ACTION",
        entityType: "aarAction",
        entityId: actionId,
        entityTitle: updatedProject.actions.find(
          (action) => String(action.id) === String(actionId)
        )?.action,
        changes: updates,
      });
    }


    setProjects(
      (currentProjects) =>

        currentProjects.map(
          (project) => {

            if (
              project.id !==
              numericProjectId
            ) {

              return project;

            }


            const currentActions =
              Array.isArray(
                project.actions
              )
                ? project.actions
                : [];


            const updatedActions =
              currentActions.map(
                (action) => {

                  if (
                    String(action.id) !==
                    String(actionId)
                  ) {

                    return action;

                  }


                  return {

                    ...action,

                    ...updates,

                  };

                }
              );


            return {

              ...project,

              actions:
                updatedActions,

            };

          }
        )

    );

  };


  // ===================================================
  // DELETE ACTION
  // ===================================================

  const deleteAction = (
    projectId,
    actionId
  ) => {

    const numericProjectId =
      Number(projectId);

    const currentProject = projects.find(
      (project) => project.id === numericProjectId
    );

    if (currentProject) {
      const updatedProject = {
        ...currentProject,
        actions: (Array.isArray(currentProject.actions)
          ? currentProject.actions
          : []
        ).filter(
          (action) => String(action.id) !== String(actionId)
        ),
      };

      cacheProjects(
        projects.map((project) =>
          project.id === numericProjectId
            ? updatedProject
            : project
        )
      );

      saveProject({
        ...updatedProject,
      }).catch(() => {
        setDataError("Unable to delete the action from Firestore.");
      });

      recordAudit({
        action: "DELETE_ACTION",
        entityType: "aarAction",
        entityId: actionId,
        entityTitle: currentProject.actions.find(
          (action) => String(action.id) === String(actionId)
        )?.action,
      });
    }


    setProjects(
      (currentProjects) =>

        currentProjects.map(
          (project) => {

            if (
              project.id !==
              numericProjectId
            ) {

              return project;

            }


            const currentActions =
              Array.isArray(
                project.actions
              )
                ? project.actions
                : [];


            return {

              ...project,

              actions:
                currentActions.filter(
                  (action) =>
                    String(action.id) !==
                    String(actionId)
                ),

            };

          }
        )

    );

  };


  // ===================================================
  // CONTEXT VALUE
  // ===================================================

  const value = {

    projects,

    isLoading,

    dataError,

    addAAR,

    addAARWithAppwriteFile,

    updateAAR,

    approveAAR,

    deleteAAR,

    getAARById,

    addAction,

    updateAction,

    deleteAction,

  };


  // ===================================================
  // PROVIDER
  // ===================================================

  return (

    <AARDataContext.Provider
      value={value}
    >

      {children}

    </AARDataContext.Provider>

  );

}


// =====================================================
// CUSTOM HOOK
// =====================================================

export function useAARData() {

  const context =
    useContext(
      AARDataContext
    );


  if (!context) {

    throw new Error(
      "useAARData must be used inside AARDataProvider"
    );

  }


  return context;

}